import { inject, Injectable } from '@angular/core';
import { DocumentService } from '@escrowtab/docprep-api-client';
import {
  AuditService,
  EjournalApiService,
  EtJournalProfileParams,
} from '@et/api';
import { AuditAction, AuditActionType, PageMarkup } from '@et/typings';
import { Store } from '@ngxs/store';
import { catchError, finalize, map, of } from 'rxjs';
import {
  SetEjournal,
  SetEjournalError,
  SetEjournalLoading,
  SetEntries,
  SetEntriesError,
  SetEntriesLoading,
  SetNotarizedAuditActions,
  SetNotarizedAuditLoaded,
  SetPageNamesByNumber,
  SetSigners,
  SetSignersError,
  SetSignersLoading,
} from '../state/ejournal-session.actions';
import { EjournalSessionSelectors } from '../state/ejournal-session.selectors';
import { EjournalEntriesService } from './ejournal-entries.service';
import { EjournalSessionCancelService } from './ejournal-session-cancel.service';
import { EsealsConverterService } from './eseals-converter.service';

@Injectable()
export class EjournalLoadService {
  private readonly ejournalApiService = inject(EjournalApiService);
  private readonly auditService = inject(AuditService);
  private readonly documentService = inject(DocumentService);
  private readonly esealsConverter = inject(EsealsConverterService);
  private readonly sessionCancel = inject(EjournalSessionCancelService);
  private readonly store = inject(Store);

  private readonly profile = this.store.selectSignal(
    EjournalSessionSelectors.getProfile,
  );

  /** Signing-state notarized actions; re-applied after API load so they stay on top. */
  private stateNotarizedAuditOverlay: AuditAction[] | null = null;
  /** Last notarized list from the audit API for the current packet. */
  private lastApiNotarizedActions: AuditAction[] = [];
  /** True once the audit API has returned for the current packet (empty is valid). */
  private notarizedAuditLoadedFromApi = false;
  /** True when page names were supplied by the caller (e.g. native sidenav); skip markup fetch. */
  private pageNamesFromCaller = false;
  /** Caller-supplied page names; re-applied after packet reset so the effect does not wipe them. */
  private callerPageNames: Record<number, string> | null = null;

  /**
   * Clears notarized overlay / API cache and caller page-name flags (packet switch).
   */
  resetPacketScopedState(): void {
    this.stateNotarizedAuditOverlay = null;
    this.lastApiNotarizedActions = [];
    this.notarizedAuditLoadedFromApi = false;
    this.pageNamesFromCaller = false;
    this.callerPageNames = null;
  }

  /**
   * Whether page names were supplied by the caller for the current packet.
   */
  hasCallerPageNames(): boolean {
    return this.pageNamesFromCaller;
  }

  /**
   * Sets page number → document name map from the caller (e.g. signing-room markup).
   * Skips / ignores the markup API fetch for the current packet selection.
   */
  setPageNamesByNumber(pageNamesByNumber: Record<number, string>) {
    this.pageNamesFromCaller = true;
    this.callerPageNames = pageNamesByNumber;
    this.store.dispatch(new SetPageNamesByNumber(pageNamesByNumber));
  }

  /**
   * Re-dispatches caller page names after a packet reset (microtask path).
   */
  reapplyCallerPageNames(): void {
    if (this.pageNamesFromCaller && this.callerPageNames) {
      this.store.dispatch(new SetPageNamesByNumber(this.callerPageNames));
    }
  }

  /**
   * Loads eJournals for the current profile and given packet; stores the first result and loads full journal detail when an id exists.
   */
  loadEjournalsByPacketId(packetId: string) {
    const profileId = this.profile()?.profileId;
    if (!profileId) {
      return;
    }
    this.store.dispatch(new SetEjournalLoading(true));
    const httpParams: EtJournalProfileParams = { profileId, packetId };
    this.ejournalApiService
      .getEjournalsListByPackets(httpParams)
      .pipe(
        this.sessionCancel.takeUntilCancel(),
        finalize(() => this.store.dispatch(new SetEjournalLoading(false))),
        map((ejournals) => ejournals[0]),
        catchError((error) => {
          this.store.dispatch(new SetEjournalError('Failed to load eJournal'));
          throw error;
        }),
      )
      .subscribe((ejournal) => {
        this.store.dispatch(new SetEjournal(ejournal ?? null));
        if (ejournal?.id) {
          this.loadFullEjournal(ejournal.id);
        }
      });
  }

  /**
   * Loads the packet audit trail and stores Notarized actions for eSeal derivation.
   * Re-applies any signing-state overlay so in-session stamps stay on top of the API list.
   */
  loadAuditLogByPacketId(packetId: string) {
    this.auditService
      .getAuditActionsByDocumentId(packetId)
      .pipe(
        this.sessionCancel.takeUntilCancel(),
        map((actions) =>
          this.esealsConverter.getCleanNotarizedAuditActions(actions ?? []),
        ),
        map((notarized) => ({ notarized, loaded: true })),
        catchError(() => of({ notarized: [] as AuditAction[], loaded: false })),
      )
      .subscribe(({ notarized, loaded }) => {
        this.lastApiNotarizedActions = notarized;
        this.notarizedAuditLoadedFromApi = loaded;
        this.dispatchMergedNotarizedActions();
      });
  }

  /**
   * Loads markup page categories for the packet and stores page → document name for eSeal defaults.
   * No-ops when page names were already supplied by the caller.
   */
  loadPageNamesByPacketId(packetId: string) {
    if (this.pageNamesFromCaller) {
      return;
    }
    this.documentService
      .getDocumentResultsExternal({ externalId: packetId })
      .pipe(
        this.sessionCancel.takeUntilCancel(),
        map((res) => {
          if (res.statusCode !== 200 || !res.data?.pages?.length) {
            return {} as Record<number, string>;
          }
          const pages = res.data.pages.flatMap((p) => {
            const pageNumber = p.pageNumber;
            if (pageNumber == null) {
              return [];
            }
            return [
              {
                page: pageNumber,
                aiPageType:
                  (p.markup as PageMarkup | undefined)?.aiPageType ?? null,
              },
            ];
          });
          return this.esealsConverter.pageNamesFromMarkupPages(pages);
        }),
        catchError(() => of({} as Record<number, string>)),
      )
      .subscribe((pageNames) => {
        if (!this.pageNamesFromCaller) {
          this.store.dispatch(new SetPageNamesByNumber(pageNames));
        }
      });
  }

  /**
   * Merges notarized audit actions from signing state into the session for eSeal derivation.
   * Identity is normalized `actionDateTime`; overlay actions replace matching API stamps.
   * Call after `setSelectedPacket` so packet reset does not clear the overlay.
   */
  loadNotarizedAuditActionsFromState(actions: AuditAction[]) {
    this.stateNotarizedAuditOverlay = this.filterNotarizedActions(actions);
    this.dispatchMergedNotarizedActions();
  }

  /**
   * Loads signers and entries for an eJournal id and updates local signers and entries signals.
   */
  private loadFullEjournal(eJournalId: string) {
    this.store.dispatch(new SetSignersLoading(true));
    this.store.dispatch(new SetEntriesLoading(true));
    this.ejournalApiService
      .getPacketEjournal(eJournalId)
      .pipe(
        this.sessionCancel.takeUntilCancel(),
        finalize(() => {
          this.store.dispatch(new SetSignersLoading(false));
          this.store.dispatch(new SetEntriesLoading(false));
        }),
        catchError((error) => {
          this.store.dispatch(new SetSignersError('Failed to load signers'));
          this.store.dispatch(new SetEntriesError('Failed to load entries'));
          throw error;
        }),
      )
      .subscribe(({ eJournalSigners, eJournalEntries, ...eJournal }) => {
        this.store.dispatch(new SetEjournal(eJournal));
        this.store.dispatch(new SetSigners(eJournalSigners));
        this.store.dispatch(
          new SetEntries(EjournalEntriesService.sortEntries(eJournalEntries)),
        );
      });
  }

  private filterNotarizedActions(actions: AuditAction[] | null): AuditAction[] {
    if (!actions?.length) {
      return [];
    }
    return actions.filter((a) => a.actionType === AuditActionType.Notarized);
  }

  /**
   * Dispatches `lastApiNotarizedActions` merged with `stateNotarizedAuditOverlay` (overlay wins).
   */
  private dispatchMergedNotarizedActions() {
    const merged = this.mergeNotarizedByDatetime(
      this.lastApiNotarizedActions,
      this.stateNotarizedAuditOverlay ?? [],
    );
    this.store.dispatch(new SetNotarizedAuditActions(merged));
    this.store.dispatch(
      new SetNotarizedAuditLoaded(this.notarizedAuditLoadedFromApi),
    );
  }

  /**
   * Upserts notarized actions by normalized notarization datetime; later list wins on conflict.
   */
  private mergeNotarizedByDatetime(
    base: AuditAction[],
    overlay: AuditAction[],
  ): AuditAction[] {
    const byDate = new Map<string, AuditAction>();
    for (const action of [...base, ...overlay]) {
      const key = this.esealsConverter.normalizeNotarizationDate(
        action.actionDateTime,
      );
      if (key) {
        byDate.set(key, action);
      }
    }
    return [...byDate.values()];
  }
}
